import { useState, useEffect, useCallback } from "react";

import axios from "axios";

import { orgSessionManager } from "@domains/auth/services/orgSessionManager";
import { apiBaseUrls } from "@platform/constants/shared-constants";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface VcsConnectionStatus {
  /** Whether the org has an active VCS connection (GitHub App installed) */
  orgConnected: boolean;
  /** Whether VCS is fully connected — org has active connection AND app has a provisioned repo */
  connected: boolean;
  /** VCS provider name (github, gitlab) */
  provider: string | null;
  /** Organization/account name on the VCS provider */
  providerOrgName: string | null;
  /** The VCS repo URL for the application (from application.vcsRepoUrl) */
  repoUrl: string | null;
}

export interface UseVcsConnectionStatusResult {
  vcsStatus: VcsConnectionStatus;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

const DEFAULT_STATUS: VcsConnectionStatus = {
  orgConnected: false,
  connected: false,
  provider: null,
  providerOrgName: null,
  repoUrl: null,
};

const token = () => orgSessionManager.getToken() ?? "";

/**
 * Hook to fetch VCS connection status for the pipeline header badge.
 *
 * Checks two sources:
 * 1. `/api/v1/vcs-connections` — is there an active GitHub/GitLab connection for this org?
 * 2. `application.vcsRepoUrl` — passed in as a prop (already fetched by parent)
 *
 * This replaces reading VCS status from deployment events, which was coupled
 * to build/deploy sagaIds and showed "pending" after initial provisioning.
 */
export function useVcsConnectionStatus(
  orgAlias: string | undefined,
  vcsRepoUrl?: string | null,
): UseVcsConnectionStatusResult {
  const [vcsStatus, setVcsStatus] = useState<VcsConnectionStatus>({
    ...DEFAULT_STATUS,
    orgConnected: false,
    connected: false,
    repoUrl: vcsRepoUrl ?? null,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchStatus = useCallback(async () => {
    if (!orgAlias) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get<
        Array<{
          provider: string;
          providerOrgName: string;
          status: string;
          isActive: boolean;
        }>
      >(`${apiBaseUrls.BACKEND}/api/v1/vcs-connections`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      const connections = Array.isArray(res.data) ? res.data : [];
      const activeConn = connections.find((c) => c.isActive);

      setVcsStatus({
        orgConnected: !!activeConn,
        connected: !!activeConn && !!vcsRepoUrl,
        provider: activeConn?.provider ?? null,
        providerOrgName: activeConn?.providerOrgName ?? null,
        repoUrl: vcsRepoUrl ?? null,
      });
    } catch {
      setError("Failed to load VCS status");
      setVcsStatus({ ...DEFAULT_STATUS, repoUrl: vcsRepoUrl ?? null });
    } finally {
      setLoading(false);
    }
  }, [orgAlias, vcsRepoUrl]);

  useEffect(() => {
    void fetchStatus();
  }, [fetchStatus]);

  return { vcsStatus, loading, error, refetch: fetchStatus };
}
